import React from 'react';
import { Vendor } from '../../types/vendor';
import { Badge } from '../ui/Badge';
import { Star, Phone, Mail, MapPin, Layers } from 'lucide-react';

interface Props {
  vendors: Vendor[];
  loading: boolean;
  onSelect: (vendor: Vendor) => void;
  onStatusChange: (vendor: Vendor, status: Vendor['status']) => void;
}

const STATUS_CONFIG: Record<Vendor['status'], { label: string; color: 'success' | 'danger' | 'warning' | 'neutral' | 'info' }> = {
  active: { label: 'Active', color: 'success' },
  inactive: { label: 'Inactive', color: 'neutral' },
  blacklisted: { label: 'Blacklisted', color: 'danger' },
  pending_approval: { label: 'Pending', color: 'warning' },
};

function RatingStars({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(s => (
        <Star
          key={s}
          className="w-3 h-3"
          fill={s <= Math.round(rating) ? '#f59e0b' : 'none'}
          stroke={s <= Math.round(rating) ? '#f59e0b' : '#475569'}
        />
      ))}
      <span className="text-xs text-slate-400 ml-1">{rating.toFixed(1)}</span>
    </div>
  );
}

function PerfBar({ value, max = 100, color }: { value: number; max?: number; color: string }) {
  const pct = Math.min(100, (value / max) * 100);
  return (
    <div className="h-1.5 rounded-full bg-slate-700 overflow-hidden">
      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
    </div>
  );
}

export const VendorTable: React.FC<Props> = ({ vendors, loading, onSelect, onStatusChange }) => {
  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="animate-pulse h-32 rounded-xl bg-slate-800/50 border border-slate-800" />
        ))}
      </div>
    );
  }

  if (!vendors.length) {
    return (
      <div className="text-center py-16 text-slate-500">
        <Layers className="w-10 h-10 mx-auto mb-3 opacity-40" />
        <p className="text-sm">No vendors found. Onboard your first vendor.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {vendors.map(vendor => {
        const statusCfg = STATUS_CONFIG[vendor.status];
        return (
          <div
            key={vendor.vendor_id}
            onClick={() => onSelect(vendor)}
            className="group cursor-pointer bg-slate-900/70 border border-slate-800 rounded-xl p-4 hover:border-sky-500/40 hover:bg-slate-800/60 transition-all"
          >
            {/* Header row */}
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-slate-100 text-sm truncate">{vendor.name}</h3>
                  <Badge label={statusCfg.label} variant={statusCfg.color} />
                </div>
                <p className="text-xs text-slate-500 mt-0.5 font-mono">{vendor.vendor_id} · TL: {vendor.trade_license_no}</p>
              </div>
              <RatingStars rating={vendor.performance.rating} />
            </div>

            {/* Contact Info */}
            <div className="flex flex-wrap gap-x-4 gap-y-1 mb-3">
              <span className="flex items-center gap-1 text-xs text-slate-400">
                <Phone className="w-3 h-3" />{vendor.contact_person}
              </span>
              <span className="flex items-center gap-1 text-xs text-slate-400">
                <Mail className="w-3 h-3" />{vendor.contact_email}
              </span>
              <span className="flex items-center gap-1 text-xs text-slate-400">
                <MapPin className="w-3 h-3" />{vendor.service_areas.map(a => a.location_name).join(', ') || '—'}
              </span>
            </div>

            {/* Category chips */}
            <div className="flex flex-wrap gap-1.5 mb-3">
              {vendor.category_mappings.map(c => (
                <span key={c.category_id} className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-sky-500/10 text-sky-400 border border-sky-500/20">
                  {c.category_name}
                </span>
              ))}
            </div>

            {/* Performance bars */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <p className="text-[10px] text-slate-500 mb-1">On-Time Rate</p>
                <PerfBar value={vendor.performance.on_time_completion_rate} color="#34d399" />
                <p className="text-[10px] text-emerald-400 mt-0.5">{vendor.performance.on_time_completion_rate.toFixed(1)}%</p>
              </div>
              <div>
                <p className="text-[10px] text-slate-500 mb-1">Inspection Pass</p>
                <PerfBar value={vendor.performance.inspection_pass_rate} color="#38bdf8" />
                <p className="text-[10px] text-sky-400 mt-0.5">{vendor.performance.inspection_pass_rate.toFixed(1)}%</p>
              </div>
              <div>
                <p className="text-[10px] text-slate-500 mb-1">Jobs Done</p>
                <p className="text-xs text-purple-400 font-semibold">{vendor.performance.total_jobs_completed}/{vendor.performance.total_jobs_assigned}</p>
              </div>
            </div>

            {/* Status action */}
            <div className="mt-3 pt-3 border-t border-slate-800 flex items-center justify-between" onClick={e => e.stopPropagation()}>
              <span className="text-[10px] text-slate-500">Avg Response: {vendor.performance.avg_response_time_hours.toFixed(1)}h</span>
              <select
                value={vendor.status}
                onChange={e => onStatusChange(vendor, e.target.value as Vendor['status'])}
                className="text-xs bg-slate-800 border border-slate-700 rounded-lg px-2 py-1 text-slate-300 focus:outline-none focus:border-sky-500"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="pending_approval">Pending</option>
                <option value="blacklisted">Blacklisted</option>
              </select>
            </div>
          </div>
        );
      })}
    </div>
  );
};
